import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, FileText, Send, Download, Trash2, RefreshCw, X, Save, Mail, Hash, Calendar, CheckCircle2, AlertCircle, Clock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { NumberStepper } from "@/components/ui/number-stepper";

const emptyItem = () => ({ description: "", quantity: 1, unit: "PZA", unit_price: 0, product_id: null });
const emptyQuote = () => ({
  mode: "external", // "client" | "external"
  user_id: "",
  recipient_email: "",
  recipient_name: "",
  recipient_company: "",
  recipient_phone: "",
  folio: "",
  validity_days: 30,
  payment_terms: "50% anticipo, 50% contra entrega",
  iva_rate: 16,
  notes: "",
  conditions: "",
  items: [emptyItem()],
  send_email: true,
});

const money = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

export default function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyQuote());
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [q, u, p] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/users"),
        api.get("/admin/products"),
      ]);
      setQuotes(q.data);
      setClients(u.data);
      setProducts(p.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Listen for "open quote dialog with prefilled data" events (from Messages tab)
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      setForm({
        ...emptyQuote(),
        mode: "external",
        recipient_email: d.recipient_email || "",
        recipient_name: d.recipient_name || "",
        recipient_company: d.recipient_company || "",
        recipient_phone: d.recipient_phone || "",
        notes: d.service ? `Cotización para: ${d.service}` : "",
        message_id: d.message_id || null,
      });
      setOpen(true);
    };
    window.addEventListener("sgm:open-quote", handler);
    return () => window.removeEventListener("sgm:open-quote", handler);
  }, []);

  const subtotal = useMemo(
    () => form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0),
    [form.items]
  );
  const iva = useMemo(() => subtotal * (Number(form.iva_rate) || 0) / 100, [subtotal, form.iva_rate]);
  const total = subtotal + iva;

  const setItem = (idx, patch) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const pickProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return setItem(idx, { product_id: null });
    setItem(idx, {
      product_id: p.id,
      description: p.description ? `${p.name} — ${p.description}` : p.name,
      unit: p.unit || "PZA",
      unit_price: Number(p.unit_price) || 0,
    });
  };

  const openCreate = () => { setForm(emptyQuote()); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    // basic validation
    if (form.mode === "client" && !form.user_id) return toast.error("Selecciona un cliente");
    if (form.mode === "external" && !form.recipient_email) return toast.error("Indica un correo destinatario");
    if (!form.items.length || form.items.some((it) => !it.description || Number(it.quantity) <= 0)) {
      return toast.error("Revisa las partidas (concepto y cantidad)");
    }
    setSaving(true);
    try {
      const payload = {
        user_id: form.mode === "client" ? form.user_id : null,
        recipient_email: form.mode === "external" ? form.recipient_email : null,
        recipient_name: form.recipient_name,
        recipient_company: form.recipient_company,
        recipient_phone: form.recipient_phone,
        folio: form.folio,
        validity_days: Number(form.validity_days) || 30,
        payment_terms: form.payment_terms,
        iva_rate: Number(form.iva_rate) || 0,
        notes: form.notes,
        conditions: form.conditions,
        send_email: form.send_email,
        items: form.items.map((it) => ({
          description: it.description.trim(),
          quantity: Number(it.quantity),
          unit: it.unit || "PZA",
          unit_price: Number(it.unit_price),
          product_id: it.product_id || null,
        })),
      };
      const { data } = await api.post("/admin/quotes", payload);
      if (form.send_email) {
        if (data.email?.ok) toast.success(`Cotización ${data.quote.folio} enviada`);
        else toast.error(`Cotización guardada pero falló el envío: ${data.email?.error || "error desconocido"}`);
      } else {
        toast.success(`Cotización ${data.quote.folio} guardada`);
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/admin/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cotizacion-${q.folio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Error al descargar PDF");
    }
  };

  const resend = async (q) => {
    if (!window.confirm(`¿Reenviar cotización ${q.folio} a ${q.recipient_email}?`)) return;
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/resend`);
      if (data.ok) toast.success("Cotización reenviada");
      else toast.error(`Error: ${data.result?.error || "envío fallido"}`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (q) => {
    if (!window.confirm(`¿Eliminar cotización ${q.folio}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/quotes/${q.id}`);
      toast.success("Cotización eliminada");
      fetchAll();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const statusBadge = (s) => {
    if (s === "sent") return { c: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5", l: "Enviada", I: CheckCircle2 };
    if (s === "failed") return { c: "text-red-400 border-red-400/40 bg-red-400/5", l: "Falló envío", I: AlertCircle };
    if (s === "skipped") return { c: "text-zinc-400 border-zinc-500/40 bg-zinc-500/5", l: "Sin enviar", I: Clock };
    return { c: "text-amber-400 border-amber-400/40 bg-amber-400/5", l: "Pendiente", I: Clock };
  };

  return (
    <div data-testid="quotes-tab">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h3 className="font-heading font-bold uppercase text-xl tracking-tight">
          <FileText className="inline w-5 h-5 text-[#F59E0B] mr-2 -mt-1" />
          Cotizaciones
        </h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openCreate} className="btn-primary" data-testid="quote-create-btn">
              <Plus className="w-4 h-4" /> Nueva Cotización
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white rounded-none p-0 w-[96vw] sm:w-[94vw] max-w-none sm:max-w-[1200px] max-h-[94vh] flex flex-col">
            <DialogHeader className="px-8 pt-7 pb-5 border-b border-[#2E2E33] flex-shrink-0">
              <DialogTitle className="font-heading font-black uppercase tracking-tight text-2xl">Nueva Cotización</DialogTitle>
              <DialogDescription className="text-[#A1A1AA] mt-1">
                Genera un PDF profesional y envíalo por correo. Folio automático si lo dejas vacío. Puedes agregar varias partidas.
              </DialogDescription>
            </DialogHeader>
            <form id="quote-form" onSubmit={submit} className="flex-1 overflow-y-auto px-8 py-6 space-y-8" data-testid="quote-form">
              {/* Recipient toggle */}
              <section>
                <div className="overline mb-3">// Destinatario</div>
                <div className="grid grid-cols-2 gap-px bg-[#2E2E33] border border-[#2E2E33] mb-5">
                  {[
                    { v: "client", l: "Cliente registrado" },
                    { v: "external", l: "Correo externo" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm({ ...form, mode: opt.v })}
                      className={`py-3.5 font-heading font-bold uppercase text-xs tracking-widest transition-colors ${
                        form.mode === opt.v ? "bg-[#F59E0B] text-black" : "bg-[#1A1A1F] text-[#A1A1AA] hover:bg-[#26262C]"
                      }`}
                      data-testid={`quote-mode-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
                {form.mode === "client" ? (
                  <div className="max-w-xl">
                    <label className="overline block mb-2">Cliente</label>
                    <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger className="input-base rounded-none" data-testid="quote-client-select">
                        <SelectValue placeholder="Selecciona cliente" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F25] border-[#2E2E33] text-white rounded-none">
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id} className="font-mono text-sm">
                            {c.email}{c.company ? ` · ${c.company}` : ""}
                          </SelectItem>
                        ))}
                        {clients.length === 0 && <SelectItem value="none" disabled>No hay clientes</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="overline block mb-2">Email *</label>
                      <input required type="email" className="input-base" value={form.recipient_email}
                        onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="quote-recipient-email" />
                    </div>
                    <div>
                      <label className="overline block mb-2">Nombre</label>
                      <input className="input-base" value={form.recipient_name}
                        onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="quote-recipient-name" />
                    </div>
                    <div>
                      <label className="overline block mb-2">Empresa</label>
                      <input className="input-base" value={form.recipient_company}
                        onChange={(e) => setForm({ ...form, recipient_company: e.target.value })} data-testid="quote-recipient-company" />
                    </div>
                    <div>
                      <label className="overline block mb-2">Teléfono</label>
                      <input className="input-base" value={form.recipient_phone}
                        onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} data-testid="quote-recipient-phone" />
                    </div>
                  </div>
                )}
              </section>

              {/* Meta */}
              <section>
                <div className="overline mb-3">// Datos de la cotización</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="overline block mb-2">Folio</label>
                    <input className="input-base" placeholder="Auto" value={form.folio}
                      onChange={(e) => setForm({ ...form, folio: e.target.value })} data-testid="quote-folio" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Vigencia (días)</label>
                    <NumberStepper min={1} value={form.validity_days}
                      onChange={(e) => setForm({ ...form, validity_days: e.target.value })} data-testid="quote-validity" />
                  </div>
                  <div>
                    <label className="overline block mb-2">IVA (%)</label>
                    <NumberStepper step={1} min={0} max={100} value={form.iva_rate}
                      onChange={(e) => setForm({ ...form, iva_rate: e.target.value })} data-testid="quote-iva" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Condiciones de pago</label>
                    <input className="input-base" value={form.payment_terms}
                      onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="quote-payment-terms" />
                  </div>
                </div>
              </section>

              {/* Items */}
              <section>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <div className="overline">// Partidas / Productos</div>
                    <p className="text-[#71717A] text-xs mt-1">Agrega tantas líneas como necesites. Usa el catálogo o escribe a mano.</p>
                  </div>
                  <button type="button" onClick={addItem} className="btn-primary py-2.5 px-4" data-testid="quote-add-item">
                    <Plus className="w-4 h-4" /> Agregar Partida
                  </button>
                </div>

                <div className="space-y-4" data-testid="quote-items-list">
                  {form.items.map((it, idx) => {
                    const lineSubtotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                    return (
                      <div key={idx} className="border border-[#2E2E33] bg-[#1A1A1F]" data-testid={`item-card-${idx}`}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2E2E33] bg-[#1F1F25]">
                          <div className="flex items-center gap-3">
                            <div className="font-heading font-black text-[#F59E0B] text-lg">#{idx + 1}</div>
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">Partida {idx + 1} de {form.items.length}</span>
                          </div>
                          <button type="button" onClick={() => removeItem(idx)} disabled={form.items.length === 1}
                            className="inline-flex items-center gap-1.5 border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`item-remove-${idx}`}
                            title={form.items.length === 1 ? "Debe quedar al menos una partida" : "Eliminar partida"}>
                            <Trash2 className="w-3 h-3" /> Quitar
                          </button>
                        </div>

                        <div className="p-5 space-y-4">
                          {/* Row 1: Concept + product selector */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                              <label className="overline block mb-2">Concepto / Descripción *</label>
                              <input className="input-base" value={it.description}
                                onChange={(e) => setItem(idx, { description: e.target.value })}
                                placeholder="Ej. Conformación del SGM para estación de servicio"
                                data-testid={`item-desc-${idx}`} />
                            </div>
                            <div>
                              <label className="overline block mb-2">Catálogo {products.length === 0 && <span className="text-[#71717A]">(vacío)</span>}</label>
                              <Select value={it.product_id || ""} onValueChange={(v) => pickProduct(idx, v)} disabled={products.length === 0}>
                                <SelectTrigger className="input-base rounded-none h-[46px]" data-testid={`item-product-${idx}`}>
                                  <SelectValue placeholder="o elige del catálogo" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#1F1F25] border-[#2E2E33] text-white rounded-none">
                                  {products.map((p) => (
                                    <SelectItem key={p.id} value={p.id} className="text-sm">
                                      {p.name} — {money(p.unit_price)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Row 2: Qty, Unit, Price, Subtotal */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <label className="overline block mb-2">Cantidad *</label>
                              <NumberStepper step={1} min={0} value={it.quantity}
                                onChange={(e) => setItem(idx, { quantity: e.target.value })} data-testid={`item-qty-${idx}`} />
                            </div>
                            <div>
                              <label className="overline block mb-2">Unidad</label>
                              <input className="input-base" value={it.unit} placeholder="PZA / SERV / HR"
                                onChange={(e) => setItem(idx, { unit: e.target.value })} data-testid={`item-unit-${idx}`} />
                            </div>
                            <div>
                              <label className="overline block mb-2">Precio Unit. (MXN)</label>
                              <input type="number" step="0.01" min="0" className="input-base" value={it.unit_price}
                                onChange={(e) => setItem(idx, { unit_price: e.target.value })} data-testid={`item-price-${idx}`} />
                            </div>
                            <div>
                              <label className="overline block mb-2">Subtotal</label>
                              <div className="h-[46px] flex items-center px-3 border border-[#2E2E33] bg-[#26262C]">
                                <span className="font-heading font-black text-[#F59E0B] text-lg" data-testid={`item-subtotal-${idx}`}>{money(lineSubtotal)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add another at bottom too — easy to find */}
                <button type="button" onClick={addItem}
                  className="mt-4 w-full border-2 border-dashed border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] py-4 font-heading font-bold uppercase text-xs tracking-widest text-[#A1A1AA] transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="quote-add-item-bottom">
                  <Plus className="w-4 h-4" /> Agregar otra partida
                </button>

                {/* Totals */}
                <div className="mt-6 flex justify-end">
                  <div className="w-full md:w-96 border border-[#2E2E33] bg-[#1A1A1F] p-5 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA] font-mono uppercase text-[10px] tracking-widest">Subtotal</span>
                      <span className="text-white font-bold">{money(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA] font-mono uppercase text-[10px] tracking-widest">IVA ({form.iva_rate}%)</span>
                      <span className="text-white font-bold">{money(iva)}</span>
                    </div>
                    <div className="border-t border-[#F59E0B] pt-3 mt-2 flex justify-between items-baseline">
                      <span className="text-[#F59E0B] font-heading font-bold uppercase text-sm tracking-widest">Total</span>
                      <span className="text-white font-heading font-black text-2xl">{money(total)}</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Notes & conditions */}
              <section>
                <div className="overline mb-3">// Notas y Condiciones</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="overline block mb-2">Notas (visibles al cliente)</label>
                    <textarea rows={4} className="input-base resize-none" value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-notes" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Condiciones generales</label>
                    <textarea rows={4} className="input-base resize-none" placeholder="Si lo dejas vacío usamos las condiciones estándar SGM" value={form.conditions}
                      onChange={(e) => setForm({ ...form, conditions: e.target.value })} data-testid="quote-conditions" />
                  </div>
                </div>
              </section>

              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={form.send_email}
                  onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                  className="w-4 h-4 accent-[#F59E0B]" data-testid="quote-send-checkbox" />
                <span className="text-sm text-[#D4D4D8]">Enviar por correo al destinatario inmediatamente (con PDF adjunto)</span>
              </label>
            </form>

            <DialogFooter className="px-8 py-5 border-t border-[#2E2E33] bg-[#141418] flex-shrink-0 gap-2">
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)} data-testid="quote-cancel-btn">
                <X className="w-4 h-4" /> Cancelar
              </button>
              <button type="submit" form="quote-form" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="quote-submit-btn">
                {saving ? "Generando..." : (form.send_email ? "Generar y Enviar" : "Generar")} <Send className="w-4 h-4" />
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quotes list */}
      <div className="border border-[#2E2E33] bg-[#1F1F25]" data-testid="quotes-list">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
          <div className="col-span-2">Folio</div>
          <div className="col-span-3">Destinatario</div>
          <div className="col-span-2">Emitida</div>
          <div className="col-span-1">Estado</div>
          <div className="col-span-2 text-right">Total</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-[#A1A1AA] font-mono text-sm">Cargando cotizaciones...</div>
        ) : quotes.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No hay cotizaciones. Crea la primera.</p>
          </div>
        ) : (
          quotes.map((q) => {
            const sb = statusBadge(q.email_status);
            const SI = sb.I;
            return (
              <div key={q.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C] items-center" data-testid={`quote-row-${q.id}`}>
                <div className="md:col-span-2 font-mono text-sm text-white flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5 text-[#F59E0B]" /> {q.folio}
                </div>
                <div className="md:col-span-3 text-sm">
                  <div className="text-white">{q.recipient_name || "—"}</div>
                  <div className="text-[#A1A1AA] font-mono text-xs flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> {q.recipient_email}
                  </div>
                  {q.recipient_company && <div className="text-[#71717A] text-xs">{q.recipient_company}</div>}
                </div>
                <div className="md:col-span-2 text-sm text-[#A1A1AA] flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> {q.issued_at_label || "—"}
                </div>
                <div className="md:col-span-1">
                  <span className={`inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${sb.c}`}>
                    <SI className="w-3 h-3" /> {sb.l}
                  </span>
                </div>
                <div className="md:col-span-2 text-right font-heading font-bold text-[#F59E0B]">{money(q.total)}</div>
                <div className="md:col-span-2 flex md:justify-end gap-2">
                  <button onClick={() => downloadPdf(q)} title="Descargar PDF" className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2" data-testid={`quote-pdf-${q.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => resend(q)} title="Reenviar" className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2" data-testid={`quote-resend-${q.id}`}>
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(q)} title="Eliminar" className="border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] p-2" data-testid={`quote-delete-${q.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
